import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SendIcon from "@mui/icons-material/Send";
import { GroupInvitation } from "../../lib/types";

interface PendingInvitationsProps {
  invitations: GroupInvitation[];
  onCancel: (invite: GroupInvitation) => void;
  onResend: (invite: GroupInvitation) => void;
  resendingIds: Set<string>;
  inviteLogs: Record<
    string,
    { id: string; sentAt?: string; status: "pending" | "sent" | "error" }[]
  >;
}

const PendingInvitations: React.FC<PendingInvitationsProps> = ({
  invitations,
  onCancel,
  onResend,
  resendingIds,
  inviteLogs,
}) => {
  return (
    <Card elevation={0} sx={{ borderRadius: 1 }}>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={700}>
            Pending invitations
          </Typography>
          {invitations.length === 0 ? (
            <Typography color="text.secondary">
              No pending invitations.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {invitations.map((invite) => {
                const entries = inviteLogs[invite.id] || [];
                return (
                  <Accordion
                    key={invite.id}
                    elevation={0}
                    sx={{ borderRadius: 1, overflow: "hidden" }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ bgcolor: "grey.900", px: 2 }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ sm: "center" }}
                        justifyContent="space-between"
                        sx={{ width: "100%", pr: 2 }}
                      >
                        <ListItemText
                          primary={invite.email}
                          secondary={`Role: ${invite.role}`}
                        />
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Tooltip title="Resend invitation" arrow>
                            <IconButton
                              component="div"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onResend(invite);
                              }}
                              disabled={resendingIds.has(invite.id)}
                              sx={{ cursor: "pointer" }}
                            >
                              {resendingIds.has(invite.id) ? (
                                <CircularProgress size={18} />
                              ) : (
                                <SendIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel invitation" arrow>
                            <IconButton
                              component="div"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onCancel(invite);
                              }}
                              sx={{ cursor: "pointer", color: "error.main" }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{ bgcolor: "grey.800", pt: 1, pb: 1 }}
                    >
                      {entries.length === 0 ? (
                        <Typography color="text.secondary">
                          No send activity yet.
                        </Typography>
                      ) : (
                        <List sx={{ pt: 0 }}>
                          {entries.map((entry) => (
                            <ListItem key={entry.id} sx={{ py: 0.5 }}>
                              <ListItemText
                                primary={
                                  entry.status === "error"
                                    ? "Send failed"
                                    : entry.status === "pending"
                                      ? "Sending invitation..."
                                      : "Invitation sent"
                                }
                                secondary={
                                  entry.sentAt
                                    ? new Date(entry.sentAt).toLocaleString()
                                    : ""
                                }
                              />
                              {entry.status === "pending" && (
                                <CircularProgress size={16} />
                              )}
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default PendingInvitations;
